import {OnChangeFn} from "@tanstack/react-table";
import classNames from "classnames";
import capitalize from "lodash/capitalize";
import {ReactNode} from "react";
import {FaPlus} from "react-icons/fa6";
import {useSearchParams} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {TwoLineShortDateTimeUser} from "@/components/datetime/TwoLineShortDateTimeUser.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {shortDuration} from "@/lib/format.tsx";
import {Incident, IncidentTime, IncidentType} from "@/lib/types/statuspages.ts";
import {IncidentCreateAction} from "@/pages/incidents/actions/IncidentCreateAction.tsx";
import {IncidentEndDate} from "@/pages/incidents/components/IncidentEndDate.tsx";
import {IncidentUpdateRowActions} from "@/pages/statuspages/tabs/incidents/actions/IncidentUpdateRowActions.tsx";
import {IncidentsTableSubRow} from "@/pages/statuspages/tabs/incidents/IncidentsTableSubRow.tsx";

import styles from "./IncidentsTable.module.scss";

interface IncidentsTableProps {
  statusPageID: number;
  incidents: Incident[];
  incidentType: IncidentType;
  incidentTime: IncidentTime;
  rowSelection: Record<string, boolean>;
  setRowSelection: OnChangeFn<Record<string, boolean>>;
  actionBar?: ReactNode;
  loading: boolean;
  isFiltered?: boolean;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationData?: unknown[];
}

export const IncidentsTable = ({
  statusPageID,
  incidents,
  incidentType,
  incidentTime,
  rowSelection,
  setRowSelection,
  actionBar,
  loading,
  isFiltered = false,
  pagination,
  totalRows,
  updatePagination,
  paginationData,
}: IncidentsTableProps) => {
  const {user} = useCurrentUserContext();
  const [searchParams] = useSearchParams();
  const default_expand_id = searchParams.get("incident_id") || "";

  const specs: ColumnSpec<Incident>[] = [
    {
      meta: {
        columnClassName: styles.nameCell,
        headerClassName: styles.nameCell,
      },
      fieldName: "name",
      fieldTitle: "Name",
      accessor: (incident: Incident) => (
        <Tooltip tooltipContent={incident.name} delayMs={500}>
          <div className={classNames("ellipsis-overflow")}>{incident.name}</div>
        </Tooltip>
      ),
    },
    {
      meta: {
        columnClassName: styles.durationCell,
        headerClassName: styles.durationCell,
      },
      fieldName: "duration",
      accessor: (obj: Incident) => {
        return shortDuration(obj.duration, {roundSeconds: true});
      },
      fieldTitle: "Duration",
    },
    {
      meta: {
        columnClassName: styles.dateCell,
        headerClassName: styles.dateCell,
      },
      fieldName: "start",
      fieldTitle: "Start",
      accessor: (incident: Incident) => <TwoLineShortDateTimeUser date={incident.starts_at.toString()} user={user!} />,
    },
    {
      meta: {
        columnClassName: styles.dateCell,
        headerClassName: styles.dateCell,
      },
      fieldName: "end",
      accessor: (incident: Incident) => (
        <IncidentEndDate endsAt={incident.ends_at} startsAt={incident.starts_at} user={user!} />
      ),
      fieldTitle: "End",
    },
  ];
  if (user?.has_write_access) {
    specs.push({
      fieldName: "actions",
      meta: {
        columnClassName: styles.actionCell,
        headerClassName: styles.actionCell,
      },
      accessor: (obj: Incident) => (
        <IncidentUpdateRowActions key={obj.ends_at} incident={obj} statusPageID={statusPageID} />
      ),
    });
  }
  return (
    <Table
      rows={incidents}
      fixedWidth
      minWidth={"800px"}
      specs={specs}
      initialState={{columnPinning: {right: ["actions"]}}}
      subRowEndSpacer
      rowSelection={rowSelection}
      setRowSelection={user?.has_write_access ? setRowSelection : undefined}
      subrowRenderer={(i: Incident) => <IncidentsTableSubRow incident={i} />}
      expandableSubRows={true}
      getRowId={(row: Incident) => row.id.toString()}
      expandedDefault={default_expand_id ? {[default_expand_id]: true} : {}}
      isLoading={loading}
      noDataComponent={
        <EmptyTable colSpan={specs.length + (rowSelection ? 1 : 0)}>
          {isFiltered ? (
            <div className={"d-flex"}>
              No {incidentType === IncidentType.INCIDENT ? "Incidents" : "Maintenance Events"} found matching the
              filter(s) above.
            </div>
          ) : (
            <div className={"d-flex flex-column align-items-center gap-3 py-5"}>
              <div className={"pulse-color-gray-800 pulseTextBodyBold"}>
                You do not have any {capitalize(IncidentTime[incidentTime].toLowerCase())}{" "}
                {incidentType === IncidentType.INCIDENT ? "Incidents" : "Maintenance Events"}
              </div>
              <IncidentCreateAction incidentType={incidentType} statusPageId={statusPageID} variant={"secondary"}>
                <Icon icon={FaPlus} />
                {incidentType === IncidentType.INCIDENT ? "Create Incident" : "Schedule Maintenance"}
              </IncidentCreateAction>
            </div>
          )}
        </EmptyTable>
      }
      actionBar={user?.has_write_access && actionBar}
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
      paginationData={paginationData}
    />
  );
};
